import { useQuery } from '@apollo/client';
import {
  GET_ACCOUNT_VERSIONS,
  GET_PRODUCTION_ORDER_VERSIONS,
  GET_SHOP_VERSIONS,
  GET_WARRANTY_VERSIONS,
} from '@retainerclub/shared-api';
import PropTypes from 'prop-types';
import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import ActivityFeed from '../../ActivityFeed/ActivityFeed';
import ActivityFilter from '../../ActivityFeed/ActivityFilter';
import Card from '../../Card/Card';
import LoadingOrError from '../../Loader/LoadingOrError';

const ACCOUNT_KLASSES = [
  'Referral',
  'User',
  'Account',
  'Patient',
  'Subscription',
];
const SHOP_KLASSES = ['Order'];
const PRODUCTION_ORDER_KLASSES = ['ProductionOrder'];
const WARRANTY_KLASSES = ['Warranty'];

const PAGE_SIZE = 25;

// Parse filters from URL search params
function parseFiltersFromParams(searchParams) {
  const filters = {};

  const dateFrom = searchParams.get('activity_dateFrom');
  const dateRange = searchParams.get('activity_dateRange');
  const eventTypes = searchParams.get('activity_eventTypes');
  const search = searchParams.get('activity_search');

  if (dateFrom) {
    filters.dateFrom = dateFrom;
  }
  if (dateRange) {
    filters.dateRange = dateRange;
  }
  if (eventTypes) {
    filters.eventTypes = eventTypes.split(',');
  }
  if (search) {
    filters.search = search;
  }

  return filters;
}

// Convert filters to URL search params
function filtersToParams(filters, currentParams) {
  const newParams = new URLSearchParams(currentParams);

  // Remove all activity params first
  newParams.delete('activity_dateFrom');
  newParams.delete('activity_dateRange');
  newParams.delete('activity_eventTypes');
  newParams.delete('activity_search');

  // Add active filters
  if (filters.dateFrom) {
    newParams.set('activity_dateFrom', filters.dateFrom);
  }
  if (filters.dateRange) {
    newParams.set('activity_dateRange', filters.dateRange);
  }
  if (filters.eventTypes?.length > 0) {
    newParams.set('activity_eventTypes', filters.eventTypes.join(','));
  }
  if (filters.search) {
    newParams.set('activity_search', filters.search);
  }

  return newParams;
}

// Convert UI filters to GraphQL filter input
function buildGraphQLFilters(filters) {
  if (!filters || Object.keys(filters).length === 0) {
    return null;
  }

  const gqlFilters = {};

  if (filters.dateFrom) {
    gqlFilters.dateFrom = filters.dateFrom;
  }
  if (filters.dateTo) {
    gqlFilters.dateTo = filters.dateTo;
  }
  if (filters.eventTypes?.length > 0) {
    gqlFilters.eventTypes = filters.eventTypes;
  }
  if (filters.search) {
    gqlFilters.search = filters.search;
  }

  return Object.keys(gqlFilters).length > 0 ? gqlFilters : null;
}

function ShowActivity({
  id = null,
  klass,
  className = '',
  showFilters = true,
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Parse filters from URL
  const filters = useMemo(
    () => parseFiltersFromParams(searchParams),
    [searchParams],
  );

  // Handle filter changes - update URL params
  const handleFilterChange = useCallback(
    (newFilters) => {
      const newParams = filtersToParams(newFilters, searchParams);
      setSearchParams(newParams, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  let dataName;

  if (ACCOUNT_KLASSES.includes(klass)) {
    dataName = 'accountVersions';
  }

  if (SHOP_KLASSES.includes(klass)) {
    dataName = 'shopVersions';
  }

  if (PRODUCTION_ORDER_KLASSES.includes(klass)) {
    dataName = 'productionOrderVersions';
  }

  if (WARRANTY_KLASSES.includes(klass)) {
    dataName = 'warrantyVersions';
  }

  function getQuery() {
    switch (klass) {
      case 'Account':
      case 'User':
      case 'Referral':
      case 'Patient':
      case 'Subscription':
        return GET_ACCOUNT_VERSIONS;
      case 'Order':
        return GET_SHOP_VERSIONS;
      case 'ProductionOrder':
        return GET_PRODUCTION_ORDER_VERSIONS;
      case 'Warranty':
        return GET_WARRANTY_VERSIONS;
      case 'PartnerLocation':
      case 'PartnerUser':
      case 'PartnerOrganization':
        return null;

      default:
        return GET_ACCOUNT_VERSIONS;
    }
  }

  const gqlFilters = useMemo(() => buildGraphQLFilters(filters), [filters]);

  const { data, loading, error, fetchMore } = useQuery(getQuery(), {
    variables: { id, klass, first: PAGE_SIZE, filters: gqlFilters },
  });

  const handleLoadMore = useCallback(async () => {
    if (!data?.[dataName]?.pageInfo?.hasNextPage || isLoadingMore) {
      return;
    }

    setIsLoadingMore(true);
    try {
      await fetchMore({
        variables: {
          after: data[dataName].pageInfo.endCursor,
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult) {
            return prev;
          }

          return {
            ...prev,
            [dataName]: {
              ...fetchMoreResult[dataName],
              nodes: [
                ...prev[dataName].nodes,
                ...fetchMoreResult[dataName].nodes,
              ],
            },
          };
        },
      });
    } finally {
      setIsLoadingMore(false);
    }
  }, [data, dataName, fetchMore, isLoadingMore]);

  const shouldRender = LoadingOrError({
    loading,
    error,
    data,
    dataKey: dataName,
    title: 'Activity',
  });

  if (shouldRender === true) {
    const activity = data[dataName]?.nodes || [];
    const pageInfo = data[dataName]?.pageInfo || {};

    return (
      <Card className={`relative p-4 ${className}`} heading="Activity">
        {showFilters && (
          <ActivityFilter
            filters={filters}
            onFilterChange={handleFilterChange}
          />
        )}
        {pageInfo.totalCount > 0 && (
          <p className="mb-2 text-gray-500 text-xs">
            Showing {activity.length} of {pageInfo.totalCount} entries
          </p>
        )}
        {pageInfo.totalCount === 0 && (
          <p className="mb-2 text-gray-500 text-xs">No activity found</p>
        )}
        <div className="max-h-[500px] overflow-y-scroll">
          <ActivityFeed
            activity={activity}
            hasNextPage={pageInfo.hasNextPage}
            isLoadingMore={isLoadingMore}
            onLoadMore={handleLoadMore}
          />
        </div>
      </Card>
    );
  }

  return shouldRender;
}

ShowActivity.propTypes = {
  id: PropTypes.string,
  klass: PropTypes.string.isRequired,
  className: PropTypes.string,
  showFilters: PropTypes.bool,
};

export default ShowActivity;
